import TrackedModel from "~/models/tracked/trackedModel";
import IConditionalRuleContainer from "~/models/conditionalRules/conditionalRuleContainer";
import AdvancedFilterConditionalRuleSet from "./advancedFilterConditionalRuleSet";
import AdvancedFilterConditionalRule from "./advancedFilterConditionalRule";
import {ConditionalFieldDefinition} from "~/models/conditionalRules/conditionalFieldDefinition";
import {Hash} from "~/interfaces/hashInterfaces";
import IConditionalRuleReferencedControl from "~/models/conditionalRules/referencedControl";
import ConditionalRuleSetFactory from "~/models/conditionalRules/conditionalRuleSetFactory";
import {RelationType} from "~/models/relationType";
import * as _ from "lodash";
import {IVOptionSetLocal} from "~/interfaces/vSelectInterfaces";
import IAdvancedFilterable from "~/interfaces/advancedFilterInterfaces";
import store from "~/state/store";
import ErrorHandler from "~/models/errorHandling/errorHandler";
import {
  ConditionalRuleBuiltInValueType,
  labelForConditionalRuleBuiltInType,
  valueForConditionalRuleBuiltInType
} from "~/models/conditionalRules/conditionalRuleBuiltInValueType";
import {IBuiltInReferencedValueRuleRootModel} from "~/models/conditionalRules/builtInReferencedValue";
import {TForm} from "~/interfaces/formInterfaces";
import AdvancedFilterEventBus from "../../modelViews/buses/advancedFilterEventBus";

const ConditionalRuleDefinition = {
  fieldName: 'filter',
  defaultConditionalValue: true,
  friendlyName: (r) => r.conditional_value,
}

export default class AdvancedFilter extends TrackedModel implements IConditionalRuleContainer, IBuiltInReferencedValueRuleRootModel {
  referencableControlContainer: IAdvancedFilterable;
  isValid: boolean = null;
  filter_conditional_rule_sets: Hash<AdvancedFilterConditionalRuleSet> = {};
  enabled: boolean;

  except = ['createEmbeddedObject', 'referencableControlContainer', 'filter_conditional_rule_sets'];

  static get routeQueryKey(): string {
    return 'advanced_filter';
  }

  constructor(referencableControlContainer: IAdvancedFilterable) {
    super();
    this.rootModel = this;
    this.referencableControlContainer = referencableControlContainer;
    this.defineProperty('enabled', true);

    this.disable = this.disable.bind(this);
  }

  createEmbeddedObject = {
    filter_conditional_rule_sets: (parent = this, json: Hash = {}): AdvancedFilterConditionalRuleSet => {
      return ConditionalRuleSetFactory.createForContainer(parent, ConditionalRuleDefinition, json, !!json.id, true) as AdvancedFilterConditionalRuleSet;
    },
  };

  get EMBEDDED_MODELS(): { [key: string]: { type: any, relationType: RelationType } } {
    return {
      filter_conditional_rule_sets: {type: AdvancedFilterConditionalRuleSet, relationType: RelationType.dictionary}
    };
  }

  disable(): void {
    if (this.enabled === false || !this.ruleSet) return;

    this.enabled = false;
  }

  get hasChanged() {
    return this._hasChanged;
  }

  set hasChanged(newVal: boolean) {
    if (newVal) {
      this.validate();
      this.queueSendChangesToServer();
    }
    this._hasChanged = newVal;
  }

  static get storeName(): string {
    return 'advancedFilterStore'
  }

  get storeName(): string {
    return AdvancedFilter.storeName
  }

  protected saveChanges(changes: any, _peekerOptions): void {
    store.commit(`${this.storeName}/saveChanges`, {changes});
  }

  saveToServer(): Promise<any> {
    return store.dispatch(`${this.storeName}/saveToServer`).then(res => {
      this.handleErrorsAndUpdateSaveMessage(res.better_errors);
    }).catch(err => {
      ErrorHandler.autosaveCatch(this, err);
    });
  }

  get ruleSet(): AdvancedFilterConditionalRuleSet {
    return this.getSortedConditionalRuleSets(ConditionalRuleDefinition.fieldName)[0];
  }

  createNewFilterRuleSet(data = {}): AdvancedFilterConditionalRuleSet {
    return this.createEmbeddedObject.filter_conditional_rule_sets(this, data);
  }

  createNewRuleSet(fieldName: string, definition: ConditionalFieldDefinition): AdvancedFilterConditionalRuleSet {
    if (this.ruleSet) {
      throw new Error('Only one rule set allowed for AdvancedFilter, and a rule set already exists!');
    }

    return new AdvancedFilterConditionalRuleSet(fieldName, definition);
  }

  getConditionalRuleSets(fieldName: string): Hash<AdvancedFilterConditionalRuleSet> {
    return this[`${fieldName}_conditional_rule_sets`];
  }

  getSortedConditionalRuleSets(fieldName: string): AdvancedFilterConditionalRuleSet[] {
    return _.values(this.getConditionalRuleSets(fieldName));
  }

  getReferencableControls(): IConditionalRuleReferencedControl[] {
    return this.referencableControlContainer.getReferencableControls().filter(c => c.isIndexed);
  }

  getReferencedControl(id: string): IConditionalRuleReferencedControl {
    return this.getReferencableControls().find(c => c.client_id === id);
  }

  get friendlyReferencableControlsName(): string {
    return this.referencableControlContainer.friendlyReferencableControlsName;
  }

  get conditionalRulesBuiltInValueOptions(): IVOptionSetLocal {
    return {
      valueKey: 'client_id',
      labelKey: 'referenceNameOrDefault',
      heading: this.referencableControlContainer.builtInValueSectionHeading,
      options: this.builtInValueVOptions(),
    }
  }

  private builtInValueVOptions(): Hash[] {
    return this.referencableControlContainer.conditionalRuleBuiltInValueTypes().map(type => {
      let reference;
      if (type === ConditionalRuleBuiltInValueType.FormSubmissionStepSubmittedOnDate) {
        reference = (this.referencableControlContainer as TForm).initialWorkflowStep;
      }

      return {
        referenceNameOrDefault: labelForConditionalRuleBuiltInType(type, reference?.titleOrDefault),
        client_id: valueForConditionalRuleBuiltInType(type, reference?.client_id),
      };
    });
  }

  getReferencedControlOrBuiltInValue(rule: AdvancedFilterConditionalRule): IConditionalRuleReferencedControl {
    return this.getReferencedControl(rule.referenced_control_id) || rule.builtInReferencedValue;
  }

  builtInValueChoiceComparisonOptions(builtInValueType: ConditionalRuleBuiltInValueType): Hash[] {
    return this.referencableControlContainer.builtInValueChoiceComparisonOptions(builtInValueType);
  }

  conditionalRuleBuiltInValueReferenceName(type: ConditionalRuleBuiltInValueType, referenceId: string): string {
    return this.referencableControlContainer.conditionalRuleBuiltInValueReferenceName(type, referenceId);
  }

  validate(): void {
    this.isValid = this.validateRuleReferencesPresent();

    AdvancedFilterEventBus.emitValidated(this.isValid);
  }

  private validateRuleReferencesPresent(): boolean {
    return !this.firstInvalidConditionalRule();
  }

  private firstInvalidConditionalRule(): AdvancedFilterConditionalRule {
    if (!this.ruleSet) return;

    return this.ruleSet.conditionalRulesArray.find(rule => {
      const referencedControl = this.getReferencedControlOrBuiltInValue(rule);
      if (!referencedControl) return true;

      return rule.hasChoiceComparisonOptionValue &&
        !referencedControl.getChoiceComparisonOptions().find(o => o.client_id === rule.value);
    });
  }
}

export type TAdvancedFilter = AdvancedFilter;